import { ManageInvestorDetailsContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/InvestorDetails';
import {
	CHIP_THEME,
	Chips as ChipsV2,
	COLOR,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { StrawberryTable } from '@dealstack/frontend/shared/components/Table/StrawberryTable/StrawberryTable';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useContext } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { getPersonBankAccounts } from '@dealstack/frontend/app/services/Person.service.ts';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { getIdentifierRows } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData.ts';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';

const BankDetailsSection = ({ className }: { className?: string }) => {
	const manageInvestorDetailsController = useContext(ManageInvestorDetailsContext);
	const { investorDetailsData } = manageInvestorDetailsController;
	const tMipManage = useTranslationMipManage();
	const tCommonUI = useTranslationCommonUi();
	const personId = investorDetailsData?.person?.id;

	const { data: personsBankAccounts } = useQuery({
		queryKey: ['bankAccounts', personId],
		queryFn: () => (personId ? getPersonBankAccounts(personId) : null),
		enabled: !!personId,
	});

	if (!investorDetailsData) {
		return null;
	}

	const bankAccounts = personsBankAccounts ?? [];

	const bankAccountHeaderArray = [
		tMipManage('investors.expanded_row.account_holder_name'),
		tMipManage('investors.expanded_row.bank_name'),
		tMipManage('investors.expanded_row.account_details', { defaultValue: 'Account details' }),
		tMipManage('investors.expanded_row.country', { defaultValue: 'Country' }),
		tMipManage('investors.expanded_row.currency', { defaultValue: 'Currency' }),
		tMipManage('investors.expanded_row.last_updated', { defaultValue: 'Last updated' }),
	];

	const dash = { element: <>&mdash;</> } as const;
	// One cell per account rather than a column per classic field: a local-format account
	// has no IBAN or SWIFT to fill them, and its sort code / CLABE had no column at all.
	const bankAccountRows = bankAccounts.map((ba) => {
		const identifierRows = getIdentifierRows(ba.identifiers);

		return [
			ba.accountHolder || dash,
			ba.bankName || dash,
			identifierRows.length
				? {
						element: (
							<div className={REPLAY_MASK_CLASS} data-sentry-mask>
								{identifierRows.map((row) => (
									<div key={row.key}>{`${row.label}: ${row.value}`}</div>
								))}
							</div>
						),
						textToCopy: identifierRows.map((row) => row.value).join(' '),
					}
				: dash,
			getCountryNameByCode(ba.countryCode) || ba.countryCode || dash,
			ba.currency || dash,
			formatStringDateToUI(ba.updatedAt, DATE_FORMATS.CALENDAR_DATE) || dash,
		];
	});

	return (
		<div className={className}>
			<BankDetailsTextLabel disableVariantMapping variant="sans/17/800">
				{tMipManage('investors.expanded_row.bank_details')}
				{bankAccounts.length === 0 && (
					<ChipsV2 color={COLOR.PEACH} chipTheme={CHIP_THEME.LIGHT} outline size="s">
						{tCommonUI('chips.not_provided')}
					</ChipsV2>
				)}
			</BankDetailsTextLabel>
			{bankAccounts.length > 0 && (
				<StrawberryTable header={bankAccountHeaderArray} body={bankAccountRows} />
			)}
		</div>
	);
};

const BankDetailsTextLabel = styled(Text)`
	display: flex;
	flex-direction: row;
	gap: 1rem;
	margin-bottom: 1rem;
`;

const Styled = styled(BankDetailsSection)`
	position: relative;

	> ${Text} {
		margin-bottom: 1rem;
	}
	margin-bottom: 2rem;
	${StrawberryTable} {
		padding-left: 1rem;
	}
`;

export { Styled as BankDetailsSection };
